import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { getPascoService } from '@src/container';
import type { PascoListItemDTO } from '@src/application/pascos/dto';

/**
 * Hook: usePendingApprovalPascos
 * Fetches PASCOs that are not approved (is_approved = false) using React Query
 *
 * @param params - Filter and pagination parameters
 * @param options - React Query options
 * @returns Query result with pending approval PASCOs as DTOs
 *
 * @example
 * ```typescript
 * const { data: { pascos, total, hasMore }, isLoading, error, refetch } = usePendingApprovalPascos({
 *     q: 'search term',
 *     university_id: 'uni-123',
 *     semester: '1',
 *     academic_year: '2023/2024',
 *     skip: 0,
 *     limit: 20
 * });
 * ```
 */
export function usePendingApprovalPascos(
    params?: {
        q?: string;
        university_id?: string;
        semester?: string;
        academic_year?: string;
        skip?: number;
        limit?: number;
    },
    options?: Omit<UseQueryOptions<{ pascos: PascoListItemDTO[]; total: number; hasMore: boolean }, Error>, 'queryKey' | 'queryFn'>
) {
    const pascoService = getPascoService();

    return useQuery({
        queryKey: ['pascos', 'pending-approval', params],
        queryFn: () => pascoService.getPendingApprovalPascos(params),
        staleTime: 5 * 1000, // 5 seconds
        ...options,
    });
}